import React, { useState, useRef } from 'react';
import {
  Download,
  Upload,
  ShieldCheck,
  AlertCircle,
  FileCheck,
  Database,
  CheckCircle2,
  RefreshCw,
  X,
  FileText,
  Lock,
  Layers,
  ChevronRight,
  Info
} from 'lucide-react';
import { CompleteKundali, Language } from '../types/kundali';
import { UserProfile } from '../lib/firebase';
import { StoredKundaliRecord } from '../lib/kundaliService';
import {
  generateBackupPayload,
  validateBackupFile,
  executeRestore,
  downloadBackupFile,
  RestorePreview,
  VedangaBackupPayload
} from '../lib/backupRestoreService';
import { logAuditEvent } from '../lib/auditService';

interface BackupRestoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  savedKundalis: CompleteKundali[];
  currentUser: UserProfile | null;
  onRestoreComplete: (updatedKundalis: CompleteKundali[]) => void;
  lang: Language;
}

export const BackupRestoreModal: React.FC<BackupRestoreModalProps> = ({
  isOpen,
  onClose,
  savedKundalis,
  currentUser,
  onRestoreComplete,
  lang
}) => {
  const [activeTab, setActiveTab] = useState<'backup' | 'restore'>('backup');
  const [backupScope, setBackupScope] = useState<'FULL' | 'KUNDALIS_ONLY' | 'REPORTS_NOTES'>('FULL');
  const [isExporting, setIsExporting] = useState(false);
  const [backupSuccessMessage, setBackupSuccessMessage] = useState<string | null>(null);

  // Restore States
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restorePreview, setRestorePreview] = useState<RestorePreview | null>(null);
  const [rawPayload, setRawPayload] = useState<VedangaBackupPayload | null>(null);
  const [restoreMode, setRestoreMode] = useState<'merge' | 'restore-as-new' | 'skip-existing' | 'overwrite'>('merge');
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreResultMessage, setRestoreResultMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const isHindi = lang === 'hi';

  // Handle Backup Creation & Download
  const handleBackupNow = () => {
    setIsExporting(true);
    setBackupSuccessMessage(null);

    try {
      const payload = generateBackupPayload(savedKundalis, currentUser, backupScope);
      downloadBackupFile(payload);

      logAuditEvent(
        'BACKUP_CREATED',
        currentUser,
        `Created ${backupScope} local backup with ${payload.recordCount.kundalis} kundalis. Checksum: ${payload.integrityChecksum}`
      );

      setBackupSuccessMessage(
        isHindi
          ? `बैकअप फ़ाइल सफलतापूर्वक डाउनलोड हो गई है! (${payload.recordCount.kundalis} कुंडलियाँ सुरक्षित)`
          : `Backup file generated and downloaded successfully! (${payload.recordCount.kundalis} Kundalis secured)`
      );
    } catch (err: any) {
      console.error('Backup creation failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle File Upload and Preview Validation
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setRestoreFile(file);
    setRestorePreview(null);
    setRawPayload(null);
    setRestoreResultMessage(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (!content) return;

      const preview = validateBackupFile(content);
      setRestorePreview(preview);

      if (preview.isValid) {
        try {
          const parsed = JSON.parse(content) as VedangaBackupPayload;
          setRawPayload(parsed);
        } catch {
          // Handled in preview
        }
      }
    };
    reader.readAsText(file);
  };

  // Handle Restore Execution
  const handleExecuteRestore = () => {
    if (!rawPayload || !restorePreview?.isValid) return;

    setIsRestoring(true);
    setRestoreResultMessage(null);

    try {
      // Format current saved kundalis to StoredKundaliRecord shape
      const existingRecords: StoredKundaliRecord[] = savedKundalis.map((k: any, idx) => ({
        id: `knd_${idx}`,
        userId: currentUser?.uid || 'user',
        userEmail: currentUser?.email || 'user@offline',
        createdByName: k.birthDetails?.name || 'Native',
        name: k.birthDetails?.name || 'Native',
        gender: k.birthDetails?.gender || 'male',
        date: k.birthDetails?.date || '1990-01-01',
        time: k.birthDetails?.time || '12:00:00',
        placeName: k.birthDetails?.placeName || 'Default',
        latitude: k.birthDetails?.latitude || 28.6139,
        longitude: k.birthDetails?.longitude || 77.2090,
        timezone: k.birthDetails?.timezoneOffsetMinutes || 330,
        chartStyle: 'north',
        tags: ['Saved'],
        notes: '',
        isPublic: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastAccessedAt: new Date().toISOString(),
        kundaliData: k
      }));

      const { mergedRecords, restoredCount, skippedCount } = executeRestore(
        rawPayload,
        restoreMode,
        existingRecords
      );

      // Extract CompleteKundali objects
      const updatedKundalis = mergedRecords.map((r) => r.kundaliData);
      onRestoreComplete(updatedKundalis);

      logAuditEvent(
        'BACKUP_RESTORED',
        currentUser,
        `Restored from backup (Mode: ${restoreMode}). ${restoredCount} restored, ${skippedCount} skipped.`
      );

      setRestoreResultMessage(
        isHindi
          ? `सफलतापूर्वक पुनर्स्थापित किया गया: ${restoredCount} कुंडलियाँ जोड़ी गईं, ${skippedCount} सुरक्षित रखी गईं।`
          : `Restoration complete: ${restoredCount} Kundalis added/updated, ${skippedCount} preserved non-destructively.`
      );
    } catch (err: any) {
      setRestoreResultMessage(`Restoration error: ${err?.message || String(err)}`);
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
      <div className="bg-astro-bg border border-astro-border rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden my-auto text-astro-text">
        {/* Header */}
        <div className="bg-astro-primary px-5 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-astro-accent" />
            <div>
              <h2 className="text-base sm:text-lg font-bold font-serif">
                {isHindi ? 'लोकल बैकअप एवं पुनर्स्थापना (Backup & Restore)' : 'Local Data Backup & Restore Hub'}
              </h2>
              <p className="text-[11px] text-white/80">
                {isHindi
                  ? 'सुरक्षित ऑफ़लाइन बैकअप फ़ाइल (.vedanga.json) बनाएं अथवा डेटा पुनर्स्थापित करें'
                  : 'User-controlled non-destructive backup exports (.vedanga.json) & integrity-verified restore'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-astro-border bg-astro-card px-5 pt-3 gap-2">
          <button
            onClick={() => setActiveTab('backup')}
            className={`pb-2.5 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'backup'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Download className="w-3.5 h-3.5" />
            <span>{isHindi ? 'बैकअप डाउनलोड करें (Backup Now)' : 'Backup My Data Now'}</span>
          </button>
          <button
            onClick={() => setActiveTab('restore')}
            className={`pb-2.5 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'restore'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            <span>{isHindi ? 'बैकअप से पुनर्स्थापित करें (Restore)' : 'Restore from Backup'}</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {activeTab === 'backup' && (
            <div className="space-y-4">
              <div className="bg-astro-primary/5 border border-astro-primary/15 rounded-xl p-3.5 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-astro-primary shrink-0 mt-0.5" />
                <div className="text-xs text-astro-text space-y-1">
                  <p className="font-semibold text-astro-primary">
                    {isHindi ? 'शून्य-डेटा हानि नीति (Zero Data Loss Protection)' : 'Zero-Data Loss Guarantee'}
                  </p>
                  <p className="text-astro-secondary-text">
                    {isHindi
                      ? 'आपकी सभी कुंडलियाँ, जन्म विवरण एवं गणना डेटा एक मानकीकृत संस्करणित JSON फ़ाइल में सुरक्षित की जाती हैं। इसमें कोई पासवर्ड या गुप्त टोकन शामिल नहीं होते।'
                      : 'Exports all saved birth charts, calculations, and analytical snapshots into a verifiable structured payload (.vedanga.json) containing CRC32 integrity checksums.'}
                  </p>
                </div>
              </div>

              {/* Scope Selector */}
              <div>
                <label className="block text-xs font-bold text-astro-text mb-1.5">
                  {isHindi ? 'बैकअप का दायरा चुनें:' : 'Select Backup Scope:'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    onClick={() => setBackupScope('FULL')}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      backupScope === 'FULL'
                        ? 'border-astro-primary bg-astro-primary/5 font-semibold text-astro-primary'
                        : 'border-astro-border bg-white text-astro-secondary-text hover:border-astro-primary/40'
                    }`}
                  >
                    <div className="text-xs font-bold">Full Backup</div>
                    <div className="text-[10px] text-astro-secondary-text mt-0.5">All Kundalis + Profiles + Settings</div>
                  </button>

                  <button
                    onClick={() => setBackupScope('KUNDALIS_ONLY')}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      backupScope === 'KUNDALIS_ONLY'
                        ? 'border-astro-primary bg-astro-primary/5 font-semibold text-astro-primary'
                        : 'border-astro-border bg-white text-astro-secondary-text hover:border-astro-primary/40'
                    }`}
                  >
                    <div className="text-xs font-bold">Kundalis Only</div>
                    <div className="text-[10px] text-astro-secondary-text mt-0.5">Saved birth charts and ephemeris</div>
                  </button>

                  <button
                    onClick={() => setBackupScope('REPORTS_NOTES')}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      backupScope === 'REPORTS_NOTES'
                        ? 'border-astro-primary bg-astro-primary/5 font-semibold text-astro-primary'
                        : 'border-astro-border bg-white text-astro-secondary-text hover:border-astro-primary/40'
                    }`}
                  >
                    <div className="text-xs font-bold">Reports & Analysis</div>
                    <div className="text-[10px] text-astro-secondary-text mt-0.5">Saved remedies and predictions</div>
                  </button>
                </div>
              </div>

              {/* Status Summary */}
              <div className="bg-astro-card rounded-xl p-3.5 border border-astro-border text-xs space-y-1.5">
                <div className="flex justify-between text-astro-secondary-text">
                  <span>Total Saved Kundalis Ready:</span>
                  <span className="font-bold text-astro-primary">{savedKundalis.length} Records</span>
                </div>
                <div className="flex justify-between text-astro-secondary-text">
                  <span>Export Engine Version:</span>
                  <span className="font-medium text-astro-text">vedicEngine-1.2-astronomy</span>
                </div>
                <div className="flex justify-between text-astro-secondary-text">
                  <span>Schema Version:</span>
                  <span className="font-medium text-astro-text">2026.08 (Parashari + Jaimini)</span>
                </div>
              </div>

              {backupSuccessMessage && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                  <span>{backupSuccessMessage}</span>
                </div>
              )}

              <button
                onClick={handleBackupNow}
                disabled={isExporting || savedKundalis.length === 0}
                className="w-full py-2.5 px-4 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs flex items-center justify-center gap-2 shadow-xs transition-all disabled:opacity-50"
              >
                {isExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 text-astro-accent" />}
                <span>{isHindi ? 'अभी बैकअप फ़ाइल डाउनलोड करें (.vedanga.json)' : 'Download Backup File Now (.vedanga.json)'}</span>
              </button>
            </div>
          )}

          {activeTab === 'restore' && (
            <div className="space-y-4">
              {/* File Upload Box */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-astro-primary/30 hover:border-astro-primary rounded-xl p-5 text-center cursor-pointer bg-astro-card/50 hover:bg-astro-card transition-all"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".json,.vedanga.json"
                  className="hidden"
                />
                <Upload className="w-8 h-8 text-astro-primary mx-auto mb-2 opacity-70" />
                <p className="text-xs font-bold text-astro-primary">
                  {restoreFile ? restoreFile.name : isHindi ? 'बैकअप फ़ाइल (.json) चुनें अथवा यहाँ खींचें' : 'Click to select or drag & drop .vedanga.json backup file'}
                </p>
                <p className="text-[10px] text-astro-secondary-text mt-1">
                  Supports Vedanga Jyotisha backup payloads version 1.0+
                </p>
              </div>

              {/* Preview & Validation Box */}
              {restorePreview && (
                <div className="space-y-3">
                  <div
                    className={`p-3.5 rounded-xl border text-xs ${
                      restorePreview.isValid
                        ? 'bg-emerald-50/70 border-emerald-200 text-emerald-900'
                        : 'bg-rose-50 border-rose-200 text-rose-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-bold mb-1">
                      {restorePreview.isValid ? (
                        <FileCheck className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-rose-600" />
                      )}
                      <span>
                        {restorePreview.isValid
                          ? 'Valid Backup Payload Verified'
                          : 'Validation Errors Detected'}
                      </span>
                    </div>

                    {restorePreview.isValid ? (
                      <div className="grid grid-cols-2 gap-2 text-[11px] mt-2">
                        <div>
                          <span className="text-astro-secondary-text">Kundalis Found:</span>{' '}
                          <span className="font-bold text-astro-primary">{restorePreview.kundaliCount}</span>
                        </div>
                        <div>
                          <span className="text-astro-secondary-text">Backup Date:</span>{' '}
                          <span className="font-medium text-astro-text">{restorePreview.timestamp.slice(0, 10)}</span>
                        </div>
                        <div>
                          <span className="text-astro-secondary-text">Exported By:</span>{' '}
                          <span className="font-medium text-astro-text">{restorePreview.exportedByEmail}</span>
                        </div>
                        <div>
                          <span className="text-astro-secondary-text">Profiles in File:</span>{' '}
                          <span className="font-medium text-astro-text">{restorePreview.sampleNames.join(', ')}</span>
                        </div>
                      </div>
                    ) : (
                      <ul className="list-disc list-inside text-[11px] mt-1 text-rose-700">
                        {restorePreview.validationErrors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {restorePreview.isValid && (
                    <div>
                      <label className="block text-xs font-bold text-astro-text mb-1.5">
                        {isHindi ? 'पुनर्स्थापना मोड (Restoration Mode):' : 'Restoration Merge Mode:'}
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                        {[
                          { id: 'merge', title: 'Non-Destructive Merge', desc: 'Safely add new records without overwriting existing' },
                          { id: 'restore-as-new', title: 'Restore as New', desc: 'Add all records with fresh IDs' },
                          { id: 'skip-existing', title: 'Skip Existing', desc: 'Only import charts not already in vault' },
                          { id: 'overwrite', title: 'Update/Overwrite', desc: 'Update matching records with backup copy' }
                        ].map((m) => (
                          <button
                            key={m.id}
                            onClick={() => setRestoreMode(m.id as any)}
                            className={`p-2.5 rounded-xl border text-left text-xs transition-all ${
                              restoreMode === m.id
                                ? 'border-astro-primary bg-astro-primary/5 font-bold text-astro-primary'
                                : 'border-astro-border bg-white text-astro-secondary-text hover:border-astro-primary/40'
                            }`}
                          >
                            <div className="font-bold text-[11px]">{m.title}</div>
                            <div className="text-[9px] text-astro-secondary-text mt-0.5 leading-tight">{m.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {restoreResultMessage && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                  <span>{restoreResultMessage}</span>
                </div>
              )}

              <button
                onClick={handleExecuteRestore}
                disabled={isRestoring || !restorePreview?.isValid}
                className="w-full py-2.5 px-4 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs flex items-center justify-center gap-2 shadow-xs transition-all disabled:opacity-50"
              >
                {isRestoring ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-astro-accent" />}
                <span>{isHindi ? 'डेटा पुनर्स्थापित करें' : 'Execute Safe Restore'}</span>
              </button>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="bg-astro-card px-5 py-3 border-t border-astro-border flex items-center justify-between text-[11px] text-astro-secondary-text">
          <div className="flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5 text-astro-primary" />
            <span>DPDP Act 2023 & Offline Privacy Compliant</span>
          </div>
          <button
            onClick={onClose}
            className="font-semibold text-astro-primary hover:underline"
          >
            {isHindi ? 'बंद करें' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};
